import { Text, Flex, Spinner, Link, Divider, List, ListItem } from '@customerio/pluma-components/react';
import type { SearchResult } from '@/services/auth/types';
import { useDocsPage } from '../use-docs-search';
import { MetadataRow } from './metadata-row';
import * as styles from './index.css';

const MAX_VISIBLE_SECTIONS = 5;

type Props = { result: Extract<SearchResult, { kind: 'docs' }> };

export function DocsPreview({ result }: Props) {
  const { data: sections, isLoading, isError } = useDocsPage(result.doc_id);

  if (isLoading) {
    return (
      <Flex alignItems="center" justifyContent="center" py="200">
        <Spinner />
      </Flex>
    );
  }

  if (isError) {
    return (
      <Text size="xs" color="subtle">
        Failed to load page details
      </Text>
    );
  }

  const firstContent = sections?.[0]?.content;
  const visibleSections = sections?.slice(0, MAX_VISIBLE_SECTIONS) ?? [];
  const remainingCount = (sections?.length ?? 0) - MAX_VISIBLE_SECTIONS;

  return (
    <>
      <MetadataRow label="Link">
        <Text size="xs">
          <Link href={result.url} isExternal>
            {result.title}
          </Link>
        </Text>
      </MetadataRow>
      <MetadataRow label="Section">
        <Text size="xs">{result.other.description}</Text>
      </MetadataRow>

      {firstContent != null ? (
        <>
          <Divider mt="075" mb="100" />
          <Text size="xs" className={styles.docsContentSnippet}>
            {firstContent.trim()}
          </Text>
        </>
      ) : null}

      {visibleSections.length > 0 ? (
        <>
          <Divider mt="075" mb="100" />
          <Flex direction="column" gap="050">
            <Text size="xs" color="subtle">
              On this page:
            </Text>
            <List styleType="disc" pl="200">
              {visibleSections.map((section) => {
                const heading = section.headings[section.headings.length - 1] ?? section.alphaHeading;
                const depth = Math.max(0, section.headings.length - 2);
                return (
                  <ListItem key={heading} ml={depth > 0 ? '200' : undefined}>
                    <Text size="xs" overflowWrap="words">
                      {heading}
                    </Text>
                  </ListItem>
                );
              })}
            </List>
            {remainingCount > 0 ? (
              <Text size="xs" color="subtle">
                and {remainingCount} more {remainingCount === 1 ? 'section' : 'sections'}
              </Text>
            ) : null}
          </Flex>
        </>
      ) : null}
    </>
  );
}
